// The customer's own return label on the suggestion panel (2026-10-23,
// server/src/ai/customer-return-tracking.js, docs/contact-view.md). Pure, so
// it is unit-testable.

import { coverKindsOf, withoutCoveredNextSteps, whatsNextOnly } from '@cxhub/shared'

// The Support step: a review item CXHub never processes. Its button opens
// Transfer to Support with the step's text as the note.
export const SUPPORT_DEDUCTION_STEP_TYPE = 'support_remove_return_label_deduction'
// A refund on an item already on an RTN (2026-10-27,
// server/src/ai/returnable-quantity.js): the same kind of Support step.
export const SUPPORT_REFUND_STEP_TYPE = 'support_refund_existing_return'
const SUPPORT_STEP_TYPES = [SUPPORT_DEDUCTION_STEP_TYPE, SUPPORT_REFUND_STEP_TYPE]

// The transfer note a Support step carries, or null for any other step.
export function supportTransferNote(action) {
  if (!SUPPORT_STEP_TYPES.includes(action?.type)) return null
  const note = String(action.params?.transferNote ?? action.label ?? '').trim()
  return note || null
}

// What's next lines from the pipeline's own-label check (which RMA the
// customer's tracking is for, when the message doesn't say).
// A line a proposed step already says (2026-10-29: tracking the return and
// choosing its stage beside the stage choice) is left out (@cxhub/shared's
// withoutCoveredNextSteps()).
export function customerTrackingNextSteps(suggestion) {
  const lines = (suggestion?.modelMeta?.customerReturnTracking?.nextSteps ?? []).filter((s) => String(s ?? '').trim())
  return withoutCoveredNextSteps(lines, coverKindsOf(suggestion?.proposedActions ?? []))
}

// What's next from the Support refund steps (2026-10-27): "Transfer to
// Support to refund <SKU> on <RTN>."
export function supportRefundNextSteps(suggestion) {
  return (suggestion?.proposedActions ?? [])
    .filter((a) => a?.type === SUPPORT_REFUND_STEP_TYPE)
    .map((a) => String(a.params?.nextStep ?? '').trim())
    .filter(Boolean)
}

// A flow's own What's next lines (2026-11-04, the pipeline's
// modelMeta.flowNextSteps, @cxhub/shared's flowNextStepsFor()): a Product
// Defect contact's wear-and-tear check, a line only, never a step.
export function flowNextSteps(suggestion) {
  return (suggestion?.modelMeta?.flowNextSteps ?? []).map((s) => String(s ?? '').trim()).filter(Boolean)
}

// The agent's What's next without a flow line, which the agent draft panel
// shows as written (with its link), first (NextStepsSection's lead).
export function withoutFlowNextSteps(steps, suggestion) {
  const flow = flowNextSteps(suggestion)
  return (steps ?? []).filter((s) => !flow.includes(String(s ?? '').trim()))
}

// The main panel's What's next: the flow's lines first (2026-11-04, Jenn:
// the wear-and-tear check leads), then the agent's steps (when its draft is
// the suggestion's), the own-label lines and the Support refund lines, once
// each. Only what the CSR wouldn't otherwise see (2026-10-29,
// @cxhub/shared's whatsNextOnly()): a line a proposed step already says
// goes, and lines that say the same thing become one.
export function whatsNextSteps(agentSteps, suggestion) {
  const out = []
  for (const s of [...flowNextSteps(suggestion), ...(agentSteps ?? []), ...customerTrackingNextSteps(suggestion), ...supportRefundNextSteps(suggestion)]) if (!out.includes(s)) out.push(s)
  return whatsNextOnly(out, suggestion?.proposedActions ?? [])
}
